import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import { getApiBaseUrl } from '@/lib/api-url'

const apiUrl = getApiBaseUrl()

interface OrganizationMember {
  id: string
  profile_id: string
  role: 'admin' | 'professional' | 'assistant'
  specialty: string | null
  is_active: boolean
  joined_at: string
  profiles: {
    id: string
    full_name: string
    email: string
    phone: string | null
  }
}

interface CreateProfessionalData {
  email: string
  name: string
  role: 'admin' | 'professional'
  specialty?: string
}

interface UpdateProfessionalData {
  id: string
  name?: string
  email?: string
  role?: 'admin' | 'professional'
  specialty?: string
  is_active?: boolean
}

interface SendProfessionalResetEmailResponse {
  success: boolean
  message: string
  email: string
}

interface GenerateProfessionalTemporaryPasswordResponse {
  success: boolean
  message: string
  temporaryPassword: string
  email: string
  note: string
}

async function fetchProfessionals(organizationId: string): Promise<OrganizationMember[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      profile_id,
      role,
      specialty,
      is_active,
      joined_at,
      profiles (
        id,
        full_name,
        email,
        phone
      )
    `)
    .eq('organization_id', organizationId)
    .in('role', ['admin', 'professional'])
    .order('joined_at', { ascending: false })

  if (error) throw error

  // Transform the data to handle Supabase's array return type for profiles
  return (data ?? []).map((member) => ({
    ...member,
    id: member.profile_id,
    profiles: Array.isArray(member.profiles) ? member.profiles[0] : member.profiles,
  })) as OrganizationMember[]
}

async function adminProfessionalsApiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('Sessao expirada. Faca login novamente.')
  }

  const response = await fetch(`${apiUrl}/api/admin/professionals${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...(init?.headers ?? {}),
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message ?? 'Falha ao processar a solicitacao.')
  }

  return response.json()
}

export function useProfessionals() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['admin-professionals', organizationId],
    queryFn: () => fetchProfessionals(organizationId!),
    enabled: !!organizationId,
  })
}

export function useCreateProfessional() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (data: CreateProfessionalData) => {
      return adminProfessionalsApiRequest('', {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          organizationId: currentOrganization?.id,
          sendCredentials: true,
        }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-professionals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
    },
  })
}

export function useUpdateProfessional() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateProfessionalData) => {
      const { id, ...payload } = data
      return adminProfessionalsApiRequest(`/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-professionals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
    },
  })
}

export function useDeactivateProfessional() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from('organization_members')
        .update({ is_active: false })
        .eq('organization_id', currentOrganization?.id)
        .eq('profile_id', profileId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-professionals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
    },
  })
}

/**
 * Reactivate a deactivated professional
 * Task: T009
 */
export function useReactivateProfessional() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from('organization_members')
        .update({ is_active: true })
        .eq('organization_id', currentOrganization?.id)
        .eq('profile_id', profileId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-professionals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
    },
  })
}

/**
 * Remove a professional from the organization.
 * This is intentionally a soft removal to preserve org-scoped integrity for
 * sessions, schedules and historical care-team links.
 * Task: T010
 */
export function useRemoveProfessional() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from('organization_members')
        .update({ is_active: false })
        .eq('organization_id', currentOrganization?.id)
        .eq('profile_id', profileId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-professionals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
    },
  })
}

export function useSendProfessionalResetEmail() {
  return useMutation({
    mutationFn: async (profileId: string) => {
      return adminProfessionalsApiRequest<SendProfessionalResetEmailResponse>(
        `/${profileId}/send-password-reset-email`,
        {
          method: 'POST',
        },
      )
    },
  })
}

export function useGenerateProfessionalTemporaryPassword() {
  return useMutation({
    mutationFn: async (profileId: string) => {
      return adminProfessionalsApiRequest<GenerateProfessionalTemporaryPasswordResponse>(
        `/${profileId}/reset-password`,
        {
          method: 'POST',
        },
      )
    },
  })
}
